import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetServicesQuery,
  useDeleteServiceMutation,
  useUpdateServiceMutation,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import Icon from "../components/Icon";
import { createExcerpt } from "../utils/excerpt";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";

const Services = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();

  // RTK Query hooks
  const { data: servicesData, isLoading: loading } = useGetServicesQuery();
  const [deleteService] = useDeleteServiceMutation();
  const [updateService] = useUpdateServiceMutation();

  // Show all services in dashboard (both active and inactive)
  const services = servicesData?.services || [];
  const [statusMessage, setStatusMessage] = useState("");
  const messageType = useMessageType(statusMessage);
  
  // Optimistic state for service status updates
  const [optimisticStatus, setOptimisticStatus] = useState({});
  
  // Clear optimistic status when actual data matches optimistic state
  useEffect(() => {
    if (services.length > 0) {
      setOptimisticStatus((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((serviceId) => {
          const service = services.find((s) => s.id === parseInt(serviceId));
          if (service && service.is_active === updated[serviceId]) {
            delete updated[serviceId];
          }
        });
        return updated;
      });
    }
  }, [services]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("servicesMainDashboard.deleteConfirmMessage"))) {
      return;
    }

    try {
      await deleteService(id).unwrap();
      setStatusMessage(t("servicesMainDashboard.serviceDeletedSuccessfully"));
    } catch (error) {
      console.error("Error deleting service:", error);
      setStatusMessage(
        error.data?.message || t("servicesMainDashboard.errorDeletingService")
      );
    }
  };

  const handleAddNew = () => {
    navigate("/dashboard/services/add");
  };

  const handleEdit = (serviceId) => {
    navigate(`/dashboard/services/edit/${serviceId}`);
  };

  const handleServiceClick = (serviceId) => {
    navigate(`/services/${serviceId}`);
  };

  const handleToggleStatus = async (service, e) => {
    e.stopPropagation();
    
    // Optimistic update - immediately change the status
    const newStatus = !service.is_active;
    setOptimisticStatus((prev) => ({
      ...prev,
      [service.id]: newStatus,
    }));
    
    try {
      const formData = new FormData();
      formData.append("title", service.title || "");
      formData.append("content", service.content || "");
      formData.append("is_active", newStatus ? "1" : "0");
      if (service.category_id) {
        formData.append("category_id", service.category_id);
      }
      if (service.title_ar) {
        formData.append("titleAr", service.title_ar);
      }
      if (service.content_ar) {
        formData.append("contentAr", service.content_ar);
      }
      if (service.keywords) {
        formData.append("keywords", service.keywords || "");
      }

      await updateService({
        id: service.id,
        body: formData,
      }).unwrap();
      
      // Don't clear optimistic status here - let useEffect handle it when data refetches
      setStatusMessage(
        service.is_active
          ? t("servicesMainDashboard.serviceDeactivatedSuccessfully") ||
              "Service deactivated successfully!"
          : t("servicesMainDashboard.serviceActivatedSuccessfully") ||
              "Service activated successfully!"
      );
    } catch (error) {
      console.error("Error updating service status:", error);
      
      // Revert optimistic update on error
      setOptimisticStatus((prev) => {
        const updated = { ...prev };
        delete updated[service.id];
        return updated;
      });
      
      setStatusMessage(
        error.data?.message ||
          t("servicesMainDashboard.errorUpdatingService") ||
          "Error updating service status"
      );
    }
  };
  
  // Get the effective status (optimistic or actual)
  const getServiceStatus = (service) => {
    if (service.id in optimisticStatus) {
      return optimisticStatus[service.id];
    }
    return service.is_active;
  };

  if (loading) {
    return (
      <div className="services">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("servicesMainDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="services">
        <div className="auth-required">
          <h2>{t("servicesMainDashboard.authenticationRequired")}</h2>
          <p>{t("servicesMainDashboard.pleaseLoginToManage")}</p>
          <button className="login-btn" onClick={() => navigate("/login")}>
            {t("servicesMainDashboard.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="services">
      <div className="page-header">
        <div className="header-content">
          <h1>{t("servicesMainDashboard.services")}</h1>
          <p>{t("servicesMainDashboard.manageYourServices")}</p>
        </div>
        <div className="header-actions">
          <button type="button" className="add-btn" onClick={handleAddNew}>
            <Icon name="plus" size="16px" />
            {t("servicesMainDashboard.addNewService")}
          </button>
        </div>
      </div>

      <StatusMessage
        message={statusMessage}
        type={messageType}
        show={!!statusMessage}
        autoClose={messageType === "success"}
        autoCloseDelay={5000}
        onClose={() => setStatusMessage("")}
      />

      <div className="services-container">
        {services.length > 0 ? (
          <div className="services-table-container">
            <table className="services-table">
              <thead>
                <tr>
                  <th>{t("servicesMainDashboard.image")}</th>
                  <th>{t("servicesMainDashboard.title")}</th>
                  <th>{t("servicesMainDashboard.category")}</th>
                  <th>{t("servicesMainDashboard.contentPreview")}</th>
                  <th>{t("servicesMainDashboard.status")}</th>
                  <th>{t("servicesMainDashboard.created")}</th>
                  <th>{t("servicesMainDashboard.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {services.map((service) => (
                  <tr
                    key={service.id}
                    className="service-row"
                    onClick={() => handleServiceClick(service.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="image-cell">
                      {service.image_url ? (
                        <img
                          src={service.image_url}
                          alt={service.title}
                          className="service-thumbnail"
                        />
                      ) : (
                        <div className="no-image">
                          <Icon name="image" size="24px" />
                        </div>
                      )}
                    </td>
                    <td className="title-cell">{service.title}</td>
                    <td className="category-cell">
                      <span className="category-badge">
                        {service.category?.title ||
                          t("servicesMainDashboard.noCategory")}
                      </span>
                    </td>
                    <td className="content-cell">
                      <div className="content-preview">
                        {createExcerpt(service.content, 15)}
                      </div>
                    </td>
                    <td className="status-cell" onClick={(e) => e.stopPropagation()}>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={getServiceStatus(service)}
                          onChange={(e) => handleToggleStatus(service, e)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td className="date-cell">
                      {new Date(service.created_at).toLocaleDateString()}
                    </td>
                    <td className="actions-cell">
                      <div
                        className="action-buttons"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(service.id)}
                          title={t("servicesMainDashboard.editService")}
                        >
                          <Icon name="edit" size="14px" />
                        </button>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(service.id)}
                          title={t("servicesMainDashboard.deleteService")}
                        >
                          <Icon name="trash" size="14px" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-services">
            <div className="empty-content">
              <Icon name="briefcase" size="48px" />
              <h3>No Services Yet</h3>
              <p>Get started by creating your first service.</p>
              <button className="add-first-service-btn" onClick={handleAddNew}>
                <Icon name="plus" size="16px" />
                Add First Service
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Services;
